import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '../components/ui';
import { toast } from '../components/toasts';
import { errorMessage, post } from '../lib/api';
import { TAB_ID } from '../lib/client-id';
import { CLOSE_REASON_LABEL } from '../lib/format';
import { useAttendanceSettings } from '../lib/queries';
import { FocusResult } from '../lib/types';
import { useSession, useSocketEvent } from './session';

export interface FocusState {
  attendanceId: string;
  sessionId: string;
  startedAt: number;
  /** voice = em ligação (o tempo conta até desligar; sem ociosidade). */
  kind: 'chat' | 'voice';
}

interface FocusApi {
  focus: FocusState | null;
  /** Abre o atendimento na tela (nova sessão; fecha a anterior no servidor). */
  open(attendanceId: string): Promise<boolean>;
  /** Avisa que esta aba vai abrir o atendimento (assumir; '*' = iniciar conversa, id ainda desconhecido). */
  expect(attendanceId: string): void;
  /** Assumir já abre a sessão: a tela só adota o resultado. */
  adopt(result: FocusResult): void;
  /** "Fechar": para o cronômetro. */
  close(reason?: 'closed_by_operator' | 'idle_timeout'): Promise<void>;
  /** A sessão acabou por ação do próprio operador (finalizar, transferir): só limpa a tela. */
  forget(attendanceId: string): void;
}

const FocusContext = createContext<FocusApi | null>(null);
export const useFocus = () => useContext(FocusContext)!;

export { TAB_ID };

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'] as const;
/** Motivos em que a sessão acabou por ação do próprio operador nesta aba: sem aviso. */
const SILENT_REASONS = new Set(['closed_by_operator', 'finished', 'transferred']);

/**
 * Sessão de foco (D3): o tempo só conta com o atendimento aberto na tela. Envia o sinal de vida a
 * cada 15 s (pelo socket; pela API se o socket cair), avisa por ociosidade e fecha sozinho se não
 * houver resposta. Se outra aba abrir outro atendimento, esta aba deixa de contar tempo.
 */
export function FocusProvider({ children }: { children: ReactNode }) {
  const { socket, connected } = useSession();
  const qc = useQueryClient();
  const [focus, setFocus] = useState<FocusState | null>(null);
  const focusRef = useRef<FocusState | null>(null);
  focusRef.current = focus;
  const [idle, setIdle] = useState<'none' | 'warning' | 'closed'>('none');
  const lastActivity = useRef(Date.now());
  /** Atendimento que esta aba está abrindo agora (a resposta da API pode chegar depois do aviso). */
  const opening = useRef<string | null>(null);

  const { data: settings } = useAttendanceSettings();

  const clear = useCallback(
    (message?: string) => {
      const current = focusRef.current;
      setFocus(null);
      if (current) {
        void qc.invalidateQueries({ queryKey: ['attendance', current.attendanceId] });
        void qc.invalidateQueries({ queryKey: ['attendances'] });
      }
      if (message) toast(message, 'info');
    },
    [qc],
  );

  const open = useCallback(async (attendanceId: string) => {
    opening.current = attendanceId;
    try {
      const r = await post<FocusResult>(`/attendances/${attendanceId}/focus`, { clientTabId: TAB_ID });
      // Em ligação (sem o bloqueio), o tempo continua no atendimento da ligação.
      const inCall = r.attendanceId !== attendanceId;
      setFocus({ attendanceId: r.attendanceId, sessionId: r.sessionId, startedAt: Date.parse(r.startedAt), kind: inCall ? 'voice' : (focusRef.current?.sessionId === r.sessionId ? focusRef.current.kind : 'chat') });
      if (inCall) toast('Você está em uma ligação: o tempo continua contando no atendimento da ligação.', 'info');
      lastActivity.current = Date.now();
      setIdle('none');
      return true;
    } catch (err) {
      toast(errorMessage(err), 'error');
      return false;
    } finally {
      opening.current = null;
    }
  }, []);

  const expect = useCallback((attendanceId: string) => {
    opening.current = attendanceId;
  }, []);

  const adopt = useCallback((r: FocusResult) => {
    opening.current = null;
    setFocus({ attendanceId: r.attendanceId, sessionId: r.sessionId, startedAt: Date.parse(r.startedAt), kind: 'chat' });
    lastActivity.current = Date.now();
    setIdle('none');
  }, []);

  const close = useCallback(
    async (reason: 'closed_by_operator' | 'idle_timeout' = 'closed_by_operator') => {
      const current = focusRef.current;
      // A sessão de voz só termina quando a ligação termina (o servidor também ignora).
      if (!current || current.kind === 'voice') return;
      setFocus(null);
      try {
        await post(`/attendances/${current.attendanceId}/blur`, { reason });
      } catch (err) {
        toast(errorMessage(err), 'error');
      }
      void qc.invalidateQueries({ queryKey: ['attendance', current.attendanceId] });
    },
    [qc],
  );

  const forget = useCallback((attendanceId: string) => {
    if (focusRef.current?.attendanceId === attendanceId) setFocus(null);
  }, []);

  // Sinal de vida a cada 15 s enquanto houver atendimento aberto.
  const interval = (settings?.heartbeatIntervalSeconds ?? 15) * 1000;
  useEffect(() => {
    if (!focus) return;
    const beat = async () => {
      const current = focusRef.current;
      if (!current) return;
      try {
        const res =
          socket && connected
            ? ((await socket.timeout(5_000).emitWithAck('focus:heartbeat', { sessionId: current.sessionId })) as { active?: boolean })
            : await post<{ active: boolean }>('/focus/heartbeat', { sessionId: current.sessionId });
        if (res.active === false && focusRef.current?.sessionId === current.sessionId) {
          clear('O cronômetro parou: a sessão deste atendimento foi encerrada. Abra o atendimento de novo para continuar contando.');
        }
      } catch {
        // Falha pontual de rede: o próximo sinal tenta de novo (o servidor tolera 45 s).
      }
    };
    const timer = setInterval(() => void beat(), interval);
    return () => clearInterval(timer);
  }, [focus, socket, connected, interval, clear]);

  // Outra aba (ou outro computador) abriu outro atendimento: a sessão daqui foi trocada.
  // Ligação atendida (sessão de voz, aberta pelo servidor): todas as abas passam a mostrar a ligação.
  useSocketEvent<{ attendanceId: string; sessionId: string; kind?: string; at?: string }>(
    'session.started',
    useCallback(
      (d) => {
        const current = focusRef.current;
        if (d.kind === 'voice') {
          opening.current = null;
          setFocus({ attendanceId: d.attendanceId, sessionId: d.sessionId, startedAt: d.at ? Date.parse(d.at) : Date.now(), kind: 'voice' });
          setIdle('none');
          return;
        }
        // Esta aba está abrindo este atendimento (ou iniciando uma conversa, quando o id ainda não é conhecido).
        if (opening.current === '*' || d.attendanceId === opening.current) return;
        if (current && d.sessionId !== current.sessionId) clear('Você abriu outro atendimento em outra aba; o cronômetro desta aba parou.');
      },
      [clear],
    ),
  );
  useSocketEvent<{ sessionId: string; reason: string }>(
    'session.ended',
    useCallback(
      (d) => {
        const current = focusRef.current;
        if (!current || d.sessionId !== current.sessionId) return;
        if (d.reason === 'call_ended') {
          // Fim da ligação: o atendimento continua na tela (contando como chat) para registrar e finalizar.
          setFocus(null);
          if (window.location.pathname.includes(current.attendanceId)) void open(current.attendanceId);
          toast('Ligação encerrada. O atendimento continua aberto para você registrar e finalizar.', 'info');
          return;
        }
        clear(SILENT_REASONS.has(d.reason) || d.reason === 'switched' ? undefined : `O cronômetro parou (${CLOSE_REASON_LABEL[d.reason] ?? d.reason}).`);
      },
      [clear, open],
    ),
  );

  // Ociosidade (P7): aviso depois de X min sem interação; fecha depois de mais Y min.
  useEffect(() => {
    const mark = () => {
      lastActivity.current = Date.now();
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, mark, { passive: true });
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, mark);
    };
  }, []);
  const warnMs = (settings?.idleWarningMinutes ?? 10) * 60_000;
  const closeMs = (settings?.idleCloseMinutes ?? 2) * 60_000;
  useEffect(() => {
    // Em ligação não há ociosidade: o tempo conta até desligar.
    if (!focus || focus.kind === 'voice') return;
    const timer = setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      if (idleFor >= warnMs + closeMs) {
        setIdle('closed');
        void close('idle_timeout');
      } else if (idleFor >= warnMs) {
        setIdle((s) => (s === 'none' ? 'warning' : s));
      }
    }, 5_000);
    return () => clearInterval(timer);
  }, [focus, warnMs, closeMs, close]);

  const value = useMemo<FocusApi>(() => ({ focus, open, expect, adopt, close, forget }), [focus, open, expect, adopt, close, forget]);

  return (
    <FocusContext.Provider value={value}>
      {children}
      <Modal
        open={idle === 'warning'}
        title="Ainda atendendo?"
        onClose={() => {
          lastActivity.current = Date.now();
          setIdle('none');
        }}
        footer={
          <>
            <Button onClick={() => void close('idle_timeout').then(() => setIdle('none'))}>Fechar atendimento</Button>
            <Button
              variant="primary"
              onClick={() => {
                lastActivity.current = Date.now();
                setIdle('none');
              }}
            >
              Continuar atendendo
            </Button>
          </>
        }
      >
        <p>Não houve interação por {settings?.idleWarningMinutes ?? 10} minutos. Sem resposta em {settings?.idleCloseMinutes ?? 2} minutos, o atendimento é fechado na tela e o cronômetro para.</p>
      </Modal>
      <Modal open={idle === 'closed'} title="Atendimento fechado por ociosidade" onClose={() => setIdle('none')} footer={<Button variant="primary" onClick={() => setIdle('none')}>Entendi</Button>}>
        <p>O cronômetro parou. Abra o atendimento de novo quando voltar.</p>
      </Modal>
    </FocusContext.Provider>
  );
}
